import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, Subject, of } from 'rxjs';
import { map, startWith, take, takeUntil, distinctUntilChanged, shareReplay, switchMap, filter } from 'rxjs/operators';
import { IotAsset } from 'src/app/core/models/iotAssetsModel.data';
import { ActiaMapService } from 'src/app/core/services/actia-map.service';
import { DataService } from 'src/app/core/services/data.service';
import { MapContainerService } from 'src/app/core/services/mapContainer.service';
import { IotAssetsService } from 'src/app/core/services/iotAssets.service';
import { AssetMappingConfig, AssetFieldConfig, extractNameFromFieldConfigKey, buildFieldConfigKey } from 'src/app/core/models/asset-config.model';
import { Store } from '@ngrx/store';
import { IotAssetState } from 'src/app/core/store/iotAssets/iotAsset.reducer';
import * as IotAssetSelectors from 'src/app/core/store/iotAssets/iotAsset.selector';
import * as IotAssetActions from 'src/app/core/store/iotAssets/iotAsset.actions';
import { LoggerService } from 'src/app/core/services/logger.service';
import { MAP_FIELD_SCOPES } from 'src/app/core/config/config.constants';
import { CatalogService } from 'src/app/core/services/catalog.service';
import { CatalogAttributeV2, CatalogResponseV2, MergedField } from 'src/app/core/models/catalog.model';
import { getApplySelectedCustomer } from 'src/app/core/store/selectors';
import { AppStateInterface } from 'src/app/appState.interface';


@Component({
  selector: 'app-side-bar-map',
  templateUrl: './side-bar-map.component.html',
  styleUrls: ['./side-bar-map.component.scss'],
})
export class ActiaSideBarMapComponent implements OnInit, OnDestroy {
  private searchSubject = new BehaviorSubject<string>(''); // BehaviorSubject per il filtro di ricerca
  private statusFilterSubject = new BehaviorSubject<string | null>(null); // BehaviorSubject per il filtro di stato
 private gpsFilterSubject = new BehaviorSubject<string>('ALL');
  isFocused = false;
  @Input() vehicles$!: Observable<IotAsset[]>; // Streaming dei veicoli in input
filteredVehicles$ = new BehaviorSubject<IotAsset[]>([]);

  searchText: string = ''; // Testo di ricerca
  selectedFilter: string | null = null; // Filtro attivo
 // Modifica showVehicleDetails per accettarlo come input
  @Input() showVehicleDetails: boolean = false;

  // Aggiungi un output per notificare quando cambia il valore
  @Output() showVehicleDetailsChange = new EventEmitter<boolean>();
  detailVehicleRow$ = new BehaviorSubject<IotAsset | null>(null); // Veicolo selezionato
  mappingConfig$: Observable<AssetMappingConfig | null>;
  sidebarFields$: Observable<{ scope: string; fields: (AssetFieldConfig & { key: string })[] }[]>;
  private cachedSidebarFields: { scope: string; fields: (AssetFieldConfig & { key: string })[] }[] = [];
  private mergedFieldsMap: { [key: string]: MergedField } = {};
  private catalogItems$ = new BehaviorSubject<CatalogAttributeV2[] | null>(null);
  private destroy$ = new Subject<void>();



  constructor(
    public mapService: ActiaMapService,
    private navmap: MapContainerService,
    private translate: TranslateService,
    private dataService: DataService,
    private iotAssetsService: IotAssetsService,
    private store: Store<AppStateInterface & { iotAssets: IotAssetState }>,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
    private catalogService: CatalogService
  ) {
    // Inizializza il servizio se non è già stato fatto
    this.iotAssetsService.initializeService();

    // Inizializza gli observable NgRx (come nel vehicle-details)
    this.mappingConfig$ = this.store.select(IotAssetSelectors.selectMappingConfig);
  }

  /**
   * Ottiene il valore di un campo specifico da un veicolo
   */
  getFieldValue(vehicle: IotAsset, fieldKey: string): any {
    // Naviga attraverso la struttura dell'oggetto usando il fieldKey
    const keys = fieldKey.split('.');
    let value = vehicle as any;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  }

  /**
   * Formatta un valore in base al tipo del campo (stessa logica di actia-vehicle-details)
   */
  formatFieldValue(value: any, field: AssetFieldConfig & { key: string }): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const type = (field.catalogType || field.type || 'string').toLowerCase();

    switch (type) {
      case 'number':
      case 'double': {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return '-';
        }
        return numValue.toString();
      }

      case 'int':
      case 'integer': {
        const intValue = parseInt(value);
        if (isNaN(intValue)) {
          return '-';
        }
        return intValue.toString();
      }

      case 'boolean':
      case 'bool': {
        const boolValue = value === true || value === 'true' || value === 1 || value === '1';
        const trueKey = `catalog.signals.${field.key}.valueTrue`;
        const falseKey = `catalog.signals.${field.key}.valueFalse`;
        const trueLabel = this.translate.instant(trueKey);
        const falseLabel = this.translate.instant(falseKey);

        if (trueLabel !== trueKey && falseLabel !== falseKey) {
          return boolValue ? trueLabel : falseLabel;
        }
        return boolValue ? 'Sì' : 'No';
      }

      case 'datetime':
      case 'date': {
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return value.toString();
          }
          return date.toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch {
          return value.toString();
        }
      }

      case 'string':
      default:
        return value.toString();
    }
  }

  /**
   * Ottiene il valore formattato con l'unità di misura per un campo della sidebar
   */
  getFormattedValueWithUnit(vehicle: IotAsset, field: AssetFieldConfig & { key: string }): string {
    const value = this.getFieldValue(vehicle, field.key);
    const formattedValue = this.formatFieldValue(value, field);
    return field.unit && formattedValue !== '-' ? `${formattedValue} ${field.unit}` : formattedValue;
  }

  gpsFilterType: 'ALL' | 'ACTIVE' | 'INACTIVE' = 'ALL';
ngOnInit(): void {
  this.translate.setDefaultLang(this.dataService.getLanguage());

  // Carica la configurazione di mapping tramite NgRx (come nel vehicle-details)
  this.store.dispatch(IotAssetActions.loadMappingConfig());

  // Carica catalog v2 e ricostruisce mergedFieldsMap ad ogni cambio di mappingConfig
  this.store.select(getApplySelectedCustomer).pipe(
    take(1),
    switchMap(customer =>
      customer?.uid
        ? this.catalogService.getCatalogForCustomer(customer.uid)
        : of({ data: [], result: '', errorDescription: null } as CatalogResponseV2)
    ),
    switchMap(catalog =>
      this.mappingConfig$.pipe(
        map(config => ({ catalog, config }))
      )
    ),
    takeUntil(this.destroy$)
  ).subscribe(({ catalog, config }) => {
    this.catalogItems$.next(catalog.data);
    const fields = this.catalogService.buildMapConfigFields(catalog.data, config);
    // Indicizzata per "scope|name" (stesso nome tecnico possibile in scope diversi), con fallback per solo nome
    this.mergedFieldsMap = {};
    Object.values(fields).forEach(scopeFields =>
      scopeFields.forEach(f => {
        this.mergedFieldsMap[buildFieldConfigKey(f.scope, f.key)] = f;
        if (!this.mergedFieldsMap[f.key]) this.mergedFieldsMap[f.key] = f;
      })
    );
  });

  // Crea observable reattivo per i campi della sidebar che si aggiorna quando cambia la configurazione
  // Chiamato UNA SOLA VOLTA - l'observable si aggiorna automaticamente quando cambia mappingConfig$
  this.initializeSidebarFields();

  // Sottoscrivi ai cambiamenti di sidebarFields$ e aggiorna la cache
  this.sidebarFields$.pipe(
    takeUntil(this.destroy$)
  ).subscribe(fields => {
    this.cachedSidebarFields = fields;
  });

  // Crea un BehaviorSubject per i cambiamenti del filtro GPS
  this.gpsFilterSubject.next(this.gpsFilterType);

  // Osserva tutti i cambiamenti: veicoli, ricerca, stato e filtro GPS
  combineLatest([
    this.vehicles$,
    this.searchSubject.asObservable().pipe(startWith('')),
    this.statusFilterSubject.asObservable(),
    this.gpsFilterSubject.asObservable() // Usa la proprietà della classe
  ]).pipe(
    takeUntil(this.destroy$)
  ).subscribe(([vehicles, searchText, statusFilter, gpsFilter]) => {
    this.applyFiltersWithData(vehicles, searchText, statusFilter, gpsFilter);
  });

  // Il resto del codice rimane invariato
  this.vehicles$.pipe(
    takeUntil(this.destroy$)
  ).subscribe((vehicles) => {
    const currentSelectedVehicle = this.detailVehicleRow$.getValue();
    const selectedVehicle = vehicles.find(
      (vehicle) => currentSelectedVehicle && vehicle.vin === currentSelectedVehicle.vin
    );
    this.detailVehicleRow$.next(selectedVehicle || null);
  });

  this.navmap.selectedIotAssetFromMap$
    .pipe(takeUntil(this.destroy$))
    .subscribe(vehicle => {
      this.logger.debug('side-bar-map', 'Selected vehicle from map', vehicle);
      if (vehicle) {
        this.scrollToVehicle(vehicle);
      }
    });
}

/**
 * Inizializza/ri-inizializza l'observable per i campi della sidebar
 */
private initializeSidebarFields(): void {
  this.sidebarFields$ = combineLatest([
    this.mappingConfig$,
    this.catalogItems$.pipe(filter((items): items is CatalogAttributeV2[] => items !== null))
  ]).pipe(
    distinctUntilChanged(),
    map(([config, catalogItems]) => {
      if (!config || !config.fields) {
        this.logger.warn('side-bar-map', 'Nessuna configurazione campi disponibile');
        return [];
      }

      const visibleCatalogNames = new Set(
        catalogItems.filter(i => i.visible !== false).map(i => i.name)
      );
      const langKey = `${localStorage.getItem('language') || 'en'}-${(localStorage.getItem('language') || 'en').toUpperCase()}`;
      const catalogLabelMap = new Map<string, string>(
        catalogItems.map(i => [i.name, i.i18nLabel?.[langKey] || i.label || i.name])
      );

      // Raggruppa i campi per scope e filtra quelli visibili nella sidebar
      const fieldsByScope: { [scope: string]: (AssetFieldConfig & { key: string })[] } = {};
      let totalSidebarFields = 0;

      // TODO TEMP DEBUG: rimuovere dopo la verifica del campo mancante in sidebar
      console.log('[SIDEBAR-DEBUG] catalogo:', catalogItems.length, 'voci |', visibleCatalogNames.size, 'visibili | config fields:', Object.keys(config.fields).length);
      Object.keys(config.fields).forEach(storageKey => {
        const fc = config.fields[storageKey];
        if (fc.showInSidebar === true || extractNameFromFieldConfigKey(storageKey).toLowerCase().includes('eontime')) {
          console.log('[SIDEBAR-DEBUG]', storageKey, '| visible:', fc.visible, '| showInSidebar:', fc.showInSidebar, '| showInDetails:', fc.showInDetails, '| nel catalogo visibile:', visibleCatalogNames.has(extractNameFromFieldConfigKey(storageKey)), '| scope:', fc.scope);
        }
      });

      Object.keys(config.fields).forEach(storageKey => {
        const fieldConfig = config.fields[storageKey];
        const key = extractNameFromFieldConfigKey(storageKey);
        if (fieldConfig.visible !== false && fieldConfig.showInSidebar === true && visibleCatalogNames.has(key)) {
          const scope = fieldConfig.scope || 'general';
          if (!fieldsByScope[scope]) {
            fieldsByScope[scope] = [];
          }
          fieldsByScope[scope].push({
            ...fieldConfig,
            label: catalogLabelMap.get(key) || fieldConfig.label,
            key: key
          });
          totalSidebarFields++;
        }
      });

      this.logger.debug('side-bar-map', `Campi configurabili per sidebar: ${totalSidebarFields} su ${Object.keys(config.fields).length}`);

      // Debug dettagliato dei campi sidebar - FOCUS sui "dati macchina"
      let datiMacchinaCount = 0;
      Object.keys(config.fields).forEach(storageKey => {
        const fieldConfig = config.fields[storageKey];
        const key = extractNameFromFieldConfigKey(storageKey);
        if (fieldConfig.showInSidebar === true) {
          if (fieldConfig.label && fieldConfig.label.toLowerCase().includes('dati')) {
            datiMacchinaCount++;
            this.logger.info('side-bar-map', `🔍 DATI MACCHINA Field: ${key} | Label: "${fieldConfig.label}" | Visible: ${fieldConfig.visible !== false} | ShowInSidebar: ${fieldConfig.showInSidebar} | Scope: ${fieldConfig.scope || 'general'} | AvailableInModels: ${JSON.stringify((fieldConfig as any).availableInModels)}`);
          } else {
            this.logger.trace('side-bar-map', `Field: ${key} | Label: "${fieldConfig.label}" | Visible: ${fieldConfig.visible !== false} | ShowInSidebar: ${fieldConfig.showInSidebar} | Scope: ${fieldConfig.scope || 'general'}`);
          }
        }
      });

      this.logger.debug('side-bar-map', `📊 Totale campi "dati macchina" con sidebar=true: ${datiMacchinaCount}`);

      // Converti in array di oggetti scope ordinati usando l'ordine salvato dal settings panel
      const orderedScopes = this.getScopeOrder(fieldsByScope);
      const result = orderedScopes
        .filter(scope => fieldsByScope[scope] && fieldsByScope[scope].length > 0)
        .map(scopeKey => ({
          scope: scopeKey,
          fields: fieldsByScope[scopeKey].sort((a, b) => {
            // Prima ordina per displayOrder se presente, poi per label alfabetico
            const orderA = a.displayOrder ?? 999999;
            const orderB = b.displayOrder ?? 999999;

            if (orderA !== orderB) {
              return orderA - orderB;
            }

            const labelA = catalogLabelMap.get(a.key) || a.label;
            const labelB = catalogLabelMap.get(b.key) || b.label;
            return labelA.localeCompare(labelB);
          })
        }));

      this.logger.debug('side-bar-map', 'Risultato finale campi per template', result);
      // TODO TEMP DEBUG: rimuovere dopo la verifica del campo mancante in sidebar
      console.log('[SIDEBAR-DEBUG] ordine gruppi salvato:', localStorage.getItem('settings-panel-scope-order'),
        '| risultato:', result.map(g => `${g.scope}: ${g.fields.map(f => f.key).join(', ')}`).join(' || '));
      return result;
    }),
    shareReplay(1) // Condivide il risultato tra più sottoscrizioni evitando ricalcoli
  );
}

// Nuovo metodo per applicare i filtri con i dati forniti
private applyFiltersWithData(vehicles: IotAsset[], searchText: string, statusFilter: string | null, gpsFilter: string): void {
  let filtered = [...vehicles];

  // Filtro GPS - Modificato per usare le coordinate GPS invece di vehicle_status
  if (gpsFilter === 'ACTIVE') {
    // Mostra solo i veicoli con coordinate GPS valide
    filtered = filtered.filter(v => v.lat_current != null && v.lon_current != null);
  } else if (gpsFilter === 'INACTIVE') {
    // Mostra solo i veicoli con coordinate GPS mancanti
    filtered = filtered.filter(v => v.lat_current == null || v.lon_current == null);
  }

  // Resto del codice rimane invariato
  // Filtro stato veicolo
  if (statusFilter) {
    filtered = filtered.filter(v => v.vehicle_status === statusFilter);
  }

  // Filtro ricerca testuale
  if (searchText) {
    const searchLower = searchText.toLowerCase();
    filtered = filtered.filter(v =>
      v.plate?.toLowerCase().includes(searchLower) ||
      `${v.drv1_firstname || ''} ${v.drv1_lastname || ''}`.toLowerCase().includes(searchLower)
    );
  }

  // Aggiorna i veicoli filtrati
  this.filteredVehicles$.next(filtered);
}
  // Aggiorna il filtro di ricerca
onSearchChange(searchText: string): void {
  this.searchSubject.next(searchText);
  // Non serve chiamare applyFilters qui perché è gestito dalla sottoscrizione
}
  // Svuota il campo di ricerca
  clearSearch(): void {
    this.searchText = '';
    this.onSearchChange('');
  }

  // Imposta il filtro di stato
 setFilter(status: string | null): void {
  this.selectedFilter = status;
  this.statusFilterSubject.next(status);
  // Non serve chiamare applyFilters qui perché è gestito dalla sottoscrizione
}

setGpsFilter(type: 'ALL' | 'ACTIVE' | 'INACTIVE'): void {
  this.gpsFilterType = type;
    // Ora puoi accedere direttamente alla proprietà della classe
    this.gpsFilterSubject.next(type);
}
applyFilters(): void {
  this.vehicles$.pipe(take(1)).subscribe(vehicles => {
    this.applyFiltersWithData(
      vehicles,
      this.searchSubject.getValue(),
      this.statusFilterSubject.getValue(),
      this.gpsFilterType
    );
  });
}

  // Mostra il pannello dei dettagli del veicolo
  openVehiclePanel(vehicle: IotAsset): void {
    this.showVehicleDetails = true;
    this.showVehicleDetailsChange.emit(true); // Notifica il componente padre
    this.detailVehicleRow$.next(vehicle);
      this.navmap.setSelectedVehicle(vehicle);
  }

  // Chiudi il pannello dei dettagli del veicolo
  closeVehiclePanel(): void {
    this.showVehicleDetails = false;
        this.showVehicleDetailsChange.emit(false); // Notifica il componente padre
  }

  // Centra la mappa sul veicolo selezionato
  centerMapOnVehicle(vehicle: IotAsset): void {
    this.mapService.centerMapOnVehicle(vehicle);
  }

    // Metodo pubblico per aggiornare il valore programmaticamente da classe
  public setShowVehicleDetails(show: boolean): void {
    this.showVehicleDetails = show;
    this.showVehicleDetailsChange.emit(show);
  }
/**
 * Calcola il tempo trascorso dall'ultimo stato registrato del veicolo
 * @param statusEnteredAt Data dell'ultimo stato registrato
 * @returns Stringa formattata con il tempo trascorso in giorni e ore
 */
getTimeSinceLastUpdate(statusEnteredAt: string): string {
  if (!statusEnteredAt) return 'Unknown';

  // Calcola la differenza tra ora e l'ultimo stato registrato
  const lastStatusTime = new Date(statusEnteredAt).getTime();
  const currentTime = new Date().getTime();
  const diffMs = currentTime - lastStatusTime;

  // Calcola giorni, ore, minuti
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const lblYears = this.translate.instant('mapPage.years');
  const lblMonths = this.translate.instant('mapPage.months');
  const lblDays = this.translate.instant('mapPage.days');
  const lblHours = this.translate.instant('mapPage.hours');
  const lblMinutes = this.translate.instant('mapPage.minutes');

  // Formatta il risultato in base alla durata
  if (days > 365) {
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    return `${years} ${lblYears} ${remainingDays} ${lblDays}`;
  } else if (days > 30) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    return `${months} ${lblMonths} ${remainingDays} ${lblDays}`;
  } else if (days > 0) {
    return `${days} ${lblDays} ${hours} ${lblHours}`;
  } else if (hours > 0) {
    return `${hours} ${lblHours} ${minutes} ${lblMinutes}`;
  } else {
    return `${minutes} ${lblMinutes}`;
  }
}

/**
 * Verifica se un veicolo non è connesso (più di 24 ore dall'ultimo aggiornamento)
 * @param statusEnteredAt Data dell'ultimo stato registrato
 * @returns true se il veicolo non è connesso da più di 24 ore
 */
isVehicleNotConnected(statusEnteredAt: string): boolean {
  if (!statusEnteredAt) return true;

  const lastStatusTime = new Date(statusEnteredAt).getTime();
  const currentTime = new Date().getTime();
  const hoursDiff = (currentTime - lastStatusTime) / (1000 * 60 * 60);

  return hoursDiff > 24;
}

getVisibleTabs(vehicle: any): string[] {
  return ['Summary', 'Journey'];
}

onInputBlur(): void {
  // Controlla esplicitamente se searchText è vuoto o undefined
  if (!this.searchText || this.searchText.trim() === '') {
    this.isFocused = false;
    this.searchText = ''; // Normalizza stringa vuota
  } else {
    this.isFocused = true;
  }
}

// Nel tuo componente della sidebar
scrollToVehicle(vehicle: IotAsset): void {
  if (!vehicle || !vehicle.vin) return;

  this.logger.debug('side-bar-map', `Scrolling to vehicle: ${vehicle.plate}, ${vehicle.vin}`);

  // Usa setTimeout per dare tempo al rendering
  setTimeout(() => {
    // Trova l'elemento per VIN
    const elementId = `vehicle-${vehicle.vin}`;
    this.logger.trace('side-bar-map', `Looking for element with ID: ${elementId}`);

    const element = document.getElementById(elementId);

    if (element) {
      this.logger.trace('side-bar-map', 'Element found, scrolling into view');

      // Scorri all'elemento con animazione
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Verifica che la classe non sia già presente
      element.classList.remove('highlight-vehicle');

      // Forza un reflow del DOM prima di aggiungere la classe
      void element.offsetWidth;

      // Aggiungi la classe per attivare l'animazione
      element.classList.add('highlight-vehicle');
      this.logger.trace('side-bar-map', 'Highlight class added');

      // Rimuovi la classe dopo l'animazione
      setTimeout(() => {
        element.classList.remove('highlight-vehicle');
        this.logger.trace('side-bar-map', 'Highlight class removed');
      }, 2000);
    } else {
      this.logger.error('side-bar-map', `Element not found with ID: ${elementId}`);
    }
  }, 100);
}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatSecondsToDHMS(seconds: number): string {
    const days = Math.floor(seconds / (3600 * 24));
    const hours = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    let result = '';
    if (days > 0) result += `${days}g `;
    if (hours > 0 || days > 0) result += `${hours}h `;
    result += `${minutes}m`;
    return result.trim();
  }

  getVehicleOfflineStatus(vehicle: IotAsset): 'none' | 'warning' | 'danger' {
    const seconds = vehicle.fromtime_last_update_event ?? 0;
    if (seconds > 3600) return 'danger';
    if (seconds > 1800) return 'warning';
    return 'none';
  }

  /**
   * Genera l'etichetta tradotta per un campo
   * Prima cerca la traduzione usando la chiave catalog.signals.{field.key}
   * Se non trova la traduzione, usa l'etichetta originale (field.label)
   */
  generateFieldLabel(field: AssetFieldConfig & { key: string }): string {
    const scoped = field.scope ? this.mergedFieldsMap[buildFieldConfigKey(field.scope, field.key)] : undefined;
    return scoped?.label || this.mergedFieldsMap[field.key]?.label || field.label || field.key;
  }

  /**
   * Verifica se un campo deve essere visibile per un veicolo specifico
   * basandosi sul can_map_name del veicolo e sulla configurazione del campo
   * Questo metodo può essere chiamato dal template per ogni veicolo/campo
   */
  isFieldVisibleForVehicle(fieldKey: string, vehicle: IotAsset): boolean {
    if (!vehicle.can_map_name) {
      // Se il veicolo non ha can_map_name, mostra tutti i campi configurati
      return true;
    }

    return this.iotAssetsService.isFieldVisibleForVehicle(fieldKey, vehicle.can_map_name);
  }

  /**
   * Ottiene la lista dei campi sidebar filtrati per un veicolo specifico
   * Utilizzabile nel template per mostrare solo i campi compatibili con il can_map_name del veicolo
   */
  /**
   * Campi da mostrare nella sidebar per un veicolo: quelli visibili nel catalogo e con la spunta
   * SIDEBAR nella configurazione mappa, raggruppati per scope (cache di sidebarFields$).
   * Comanda il catalogo: un campo configurato compare anche se il veicolo non ha il dato
   * (valore "-"), esattamente come nel popup del marker e nella scheda Dettagli veicolo.
   * Il filtro per modello CAN (availableInModels) non viene applicato qui, altrimenti la sidebar
   * nascondeva campi che popup e dettagli mostrano.
   */
  getVisibleSidebarFieldsForVehicle(_vehicle: IotAsset): { scope: string; fields: (AssetFieldConfig & { key: string })[] }[] {
    return this.cachedSidebarFields;
  }

  /**
   * Ottiene l'ordine degli scope dal localStorage, con fallback all'ordine di default
   */
  private getScopeOrder(fieldsByScope: { [scope: string]: any[] }): string[] {
    try {
      // Carica l'ordine salvato dal settings panel
      const saved = localStorage.getItem('settings-panel-scope-order');
      if (saved) {
        const savedOrder = JSON.parse(saved);
        // Verifica che gli scope salvati siano ancora validi e presenti nei dati
        const validScopes = savedOrder.filter((scope: string) =>
          fieldsByScope[scope] && fieldsByScope[scope].length > 0
        );

        // Aggiungi eventuali nuovi scope alla fine
        const allScopes = Object.keys(fieldsByScope);
        const newScopes = allScopes.filter(scope => !validScopes.includes(scope));

        return [...validScopes, ...newScopes];
      }
    } catch (error) {
      this.logger.error('side-bar-map', 'Errore nel caricamento ordine scope:', error);
    }

    // Fallback all'ordine di default; gli scope non previsti (es. kpi, kpi_custom) vanno in coda,
    // non vanno persi: altrimenti i loro campi sparivano dalla sidebar
    const defaultOrder: string[] = [...MAP_FIELD_SCOPES, 'kpi', 'kpi_custom', 'common', 'model', 'other', 'general'];
    const present = Object.keys(fieldsByScope).filter(scope => fieldsByScope[scope] && fieldsByScope[scope].length > 0);
    const ordered = defaultOrder.filter(scope => present.includes(scope));
    const remaining = present.filter(scope => !ordered.includes(scope));
    return [...ordered, ...remaining];
  }
}
